import DataTable from '@/components/features/data/DataTable'
import { userColumns } from '@/components/features/data/DataTable/ColumnDefinitions/UserColumns'
import Modal from '@/components/layout/Modal'
import ConfirmationDialog from '@/components/utils/ConfirmationDialog'
import NotFound from '@/components/utils/NotFound'
import RouteLoadFailed from '@/components/utils/RouteLoadFailed'
import { useClient } from '@/context/ClientContext'
import { useOrganisationUsersWithUserMap } from '@/hooks/useOrganisationUsersWithUserMap'
import { throwLoaderError } from '@/lib/loader-errors'
import { ArrowLeftIcon, PlusCircleIcon } from '@heroicons/react/24/outline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import React, { useState } from 'react'

import { Select } from '@droidmap/frontend-ui'
import { Button } from '@droidmap/frontend-ui'
import type { OrganisationUser } from '@droidmap/organisation-service-contract'

interface OrganisationUserWithId extends OrganisationUser {
  id: string
}

export const Route = createFileRoute(
  '/_authenticated/orgs/$orgId/groups/$groupId',
)({
  component: GroupView,
  params: {
    parse: (params) => ({
      groupId: params.groupId,
    }),
  },
  loader: async ({ params, context }) => {
    const { groupId } = params
    const { organisationClient } = context.client
    try {
      const group = await organisationClient.getGroup(groupId, context.metadata)
      return {
        crumb: group.name,
        group,
        metadata: context.metadata,
      }
    } catch (error) {
      // A 404/403 means the group is gone or isn't ours; anything else — a
      // 5xx, a network blip — is a failure to load, not a missing group
      // (#2658), and gets the retryable error screen instead.
      throwLoaderError(error, 'group')
    }
  },
  notFoundComponent: () => <NotFound resource="Group" />,
  errorComponent: () => <RouteLoadFailed resource="group" />,
})

function GroupView() {
  const { orgId, groupId } = Route.useParams()
  const { metadata } = Route.useLoaderData()
  const queryClient = useQueryClient()
  const [addMemberOpen, setAddMemberOpen] = useState(false)
  const [selectedMemberId, setSelectedMemberId] = useState<string>('')
  const [addMemberError, setAddMemberError] = useState<string | null>(null)
  const [isAddingMember, setIsAddingMember] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [memberToDelete, setMemberToDelete] =
    useState<OrganisationUserWithId | null>(null)

  const { organisationClient, userClient } = useClient()

  const navigate = Route.useNavigate()

  const {
    orgUsers,
    userMap,
    isLoading: isLoadingOrgUsers,
  } = useOrganisationUsersWithUserMap({
    orgId,
    organisationClient,
    userClient,
    metadata,
  })

  // Fetch group details
  const { group } = Route.useLoaderData()

  // Fetch group members (organisation users in group)
  const { data: groupMembersPaged, isLoading: isLoadingGroupMembers } =
    useQuery({
      queryKey: ['groupMembers', groupId],
      queryFn: async () => {
        if (!groupId) return { items: [], next: undefined, prev: undefined }
        const response = await organisationClient.listOrganisationUsersByGroup(
          {
            groupId: groupId,
            pageSize: 10,
          },
          metadata,
        )
        return response
      },
      enabled: !!groupId,
    })

  // Prepare DataTable rows
  const groupMembers: OrganisationUserWithId[] = (
    groupMembersPaged?.items || []
  ).map((u) => ({ ...u, id: u.userId }))

  /* ------------------------- Group Member Mutations ------------------------- */

  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: async (event: React.FormEvent) => {
      event.preventDefault()
      setAddMemberError(null)
      setIsAddingMember(true)
      if (!selectedMemberId) {
        setAddMemberError('Please select a member to add.')
        setIsAddingMember(false)
        return
      }
      await organisationClient.createGroupUser(
        {
          groupId: groupId,
          userId: selectedMemberId,
          orgId: orgId,
        },
        metadata,
      )
    },
    onSuccess: () => {
      setSelectedMemberId('')
      setAddMemberOpen(false)
      setIsAddingMember(false)
      setAddMemberError(null)
      queryClient.invalidateQueries({
        queryKey: ['groupMembers', groupId],
      })
    },
    onError: () => {
      setIsAddingMember(false)
    },
  })

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: async (user: OrganisationUserWithId) => {
      await organisationClient.deleteGroupUser(
        {
          groupId: groupId,
          userId: user.userId,
          orgId: user.orgId,
        },
        metadata,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['groupMembers', groupId],
      })
      setDeleteDialogOpen(false)
      setMemberToDelete(null)
    },
  })

  // Build list of valid org members not already in the group
  const groupMemberIds = new Set(groupMembers.map((u) => u.userId))
  const validOrgMembers = orgUsers.filter((u) => !groupMemberIds.has(u.userId))

  return (
    <div>
      {/* Add Member Modal */}
      <Modal
        title="Add Group Member"
        description="Select an existing organisation member to add to this group."
        icon={<PlusCircleIcon />}
        isOpen={addMemberOpen}
        onClose={() => {
          setAddMemberOpen(false)
          setSelectedMemberId('')
          setAddMemberError(null)
          setIsAddingMember(false)
        }}
      >
        <form onSubmit={addMemberMutation.mutate} className="space-y-6">
          <Select
            label="Organisation Member"
            id="member"
            name="member"
            value={selectedMemberId}
            onChange={(e) =>
              setSelectedMemberId((e.target as HTMLSelectElement).value)
            }
            autoFocus={true}
            disabled={isLoadingOrgUsers}
          >
            <option value="">Select a member</option>
            {validOrgMembers.map((member) => {
              const user = userMap[member.userId]
              const label = user
                ? `${user.firstName || ''} ${user.lastName || ''} (${user.userEmail || member.userId})`
                : member.userId
              return (
                <option key={member.userId} value={member.userId}>
                  {label}
                </option>
              )
            })}
          </Select>
          {addMemberError && (
            <div className="text-text text-sm">{addMemberError}</div>
          )}
          <div className="mt-5 flex flex-row-reverse gap-2 sm:mt-4">
            <Button
              type="submit"
              variant="creative"
              disabled={!selectedMemberId || isAddingMember}
              isLoading={isAddingMember}
            >
              Add
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setAddMemberOpen(false)
                setSelectedMemberId('')
                setAddMemberError(null)
                setIsAddingMember(false)
              }}
              disabled={isAddingMember}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {/* Group Details */}
      <div className="flex-col space-y-6">
        <div className="rounded-lg bg-panel text-text shadow-xs p-4 flex justify-between items-center">
          <div className="w-auto">
            <h1 className="text-2xl font-semibold">
              {group?.name || 'Group Details'}
            </h1>
            <p className="mt-2 text-sm text-text/75">ID: {group?.id}</p>
          </div>
          <div className="justify-end">
            <Button
              type="button"
              variant="default"
              onClick={() => navigate({ to: `/orgs/${orgId}/groups` })}
            >
              <div className="flex items-center gap-2">
                <ArrowLeftIcon className="h-5 w-5" />
                Back to Groups
              </div>
            </Button>
          </div>
        </div>

        {/* Group Members Table */}
        <div className="mt-6">
          <DataTable
            data={groupMembers}
            columns={userColumns}
            title={`Group Members`}
            description={`Add or remove members from this group.`}
            isLoading={isLoadingGroupMembers}
            onCreate={() => setAddMemberOpen(true)}
            createButtonText="Add"
            tableOptions={{
              meta: {
                userMap,
                actions: {
                  onDelete: (user: OrganisationUserWithId) => {
                    setMemberToDelete(user)
                    setDeleteDialogOpen(true)
                  },
                },
              },
            }}
          />
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        title="Remove Group Member"
        description={(() => {
          const user = memberToDelete
            ? userMap[memberToDelete.userId]
            : undefined
          const name =
            (user &&
              [user.firstName, user.lastName].filter(Boolean).join(' ')) ||
            user?.userEmail ||
            memberToDelete?.userId ||
            'this member'
          return (
            <>
              You&rsquo;re about to remove{' '}
              <strong className="font-semibold text-text">{name}</strong> from
              this group. They&rsquo;ll lose any access granted through it. They
              can be added back later.
            </>
          )
        })()}
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false)
          setMemberToDelete(null)
        }}
        onConfirm={() => {
          if (memberToDelete) {
            removeMemberMutation.mutate(memberToDelete)
          }
        }}
        confirmText={removeMemberMutation.isPending ? 'Removing...' : 'Remove'}
        cancelText="Cancel"
        variant="destructive"
        isLoading={removeMemberMutation.isPending}
      />
    </div>
  )
}
